import React, { useMemo, useState } from 'react';
import { formatNumber } from '../format';
import { PRIMARY_COLOR } from '../products';
import Legend from './Legend';

/**
 * Lista a barre orizzontali ordinata (paesi, prodotti, provider...).
 * Leggibile anche su smartphone perché le etichette non devono stare sotto le colonne.
 *
 * @param {Array} items [{ key, label, value, segments?: [{ key, value }] }]
 * @param {Array} series definizione dei segmenti impilati [{ key, label, color }] (opzionale)
 * @param {Number} limit numero di righe visibili, le altre vengono accorpate in "Altro"
 * @param {String} color colore delle barre quando non ci sono segmenti
 */
export default function BarList({ items, series, formatValue = formatNumber, limit = 8, color = PRIMARY_COLOR }) {
    const [expanded, setExpanded] = useState(false);

    const rows = useMemo(() => {
        if (expanded || items.length <= limit + 1) return items;

        const rest = items.slice(limit);
        const other = {
            key: '__other',
            label: `Altro (${rest.length})`,
            value: rest.reduce((sum, item) => sum + item.value, 0),
            segments: series && series.map(serie => ({
                key: serie.key,
                value: rest.reduce((sum, item) => sum + ((item.segments || []).find(s => s.key === serie.key) || { value: 0 }).value, 0)
            }))
        };

        return [...items.slice(0, limit), other];
    }, [items, series, limit, expanded]);

    const max = Math.max(1, ...rows.map(row => row.value));
    const colorOf = (key) => (series.find(serie => serie.key === key) || {}).color;

    return (
        <div className="stBarList">
            {series && series.length > 1 && <Legend items={series} />}

            <ul>
                {
                    rows.map(row => (
                        <li className="stBarRow" key={row.key}>
                            <span className="stBarLabel" title={row.label}>{row.label}</span>

                            <span className="stBarTrack" aria-hidden="true">
                                <span className="stBarFill" style={{ width: `${Math.max(0.5, (row.value / max) * 100)}%` }}>
                                    {
                                        series
                                            ? row.segments.filter(s => s.value > 0).map(segment => (
                                                <span
                                                    key={segment.key}
                                                    className="stBarSegment"
                                                    style={{ flexGrow: segment.value, background: colorOf(segment.key) }}
                                                    title={`${(series.find(s => s.key === segment.key) || {}).label}: ${formatValue(segment.value)}`}
                                                />
                                            ))
                                            : <span className="stBarSegment" style={{ flexGrow: 1, background: color }} />
                                    }
                                </span>
                            </span>

                            <span className="stBarValue">{formatValue(row.value)}</span>
                        </li>
                    ))
                }
            </ul>

            {
                items.length > limit + 1 &&
                <button type="button" className="stLinkButton" onClick={() => setExpanded(!expanded)}>
                    {expanded ? 'Mostra meno' : `Mostra tutti (${items.length})`}
                </button>
            }
        </div>
    );
}
